import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Fade,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Home as ResidentialIcon,
  Business as CommercialIcon,
  Agriculture as AgriculturalIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon
} from '@mui/icons-material';
import { Pageheader } from './global-components/page-header';
import EnhancedPropertyCard from './EnhancedPropertyCard';
import PropertyFilter from './common/PropertyFilter';
import Toast from './Toast';
import NoPropertiesAnimation from './common/NoPropertiesAnimation';
import {
  getFeaturedProperties,
  getFeaturedPropertiesByCategory,
  getFeaturedPropertiesCount
} from '../services/propertyService';
import bgImage from '../assets/img/bg/property-management.webp';
import '../assets/css/featured-properties.css';

const FeaturedPropertiesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [counts, setCounts] = useState({ total: 0, residential: 0, commercial: 0, agricultural: 0 });
  const [filters, setFilters] = useState({
    featured: 'true',
    country: 'India'
  });

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');

  // Tab configuration
  const tabs = [
    {
      label: 'All Featured',
      value: 'all',
      icon: <StarIcon />,
      color: '#d8b035',
      description: 'Premium properties across all categories'
    },
    {
      label: 'Residential',
      value: 'residential',
      icon: <ResidentialIcon />,
      color: '#4caf50',
      description: 'Luxury homes, apartments & villas'
    },
    {
      label: 'Commercial',
      value: 'commercial',
      icon: <CommercialIcon />,
      color: '#2196f3',
      description: 'Office spaces, retail & business properties'
    },
    {
      label: 'Agricultural',
      value: 'agricultural',
      icon: <AgriculturalIcon />,
      color: '#ff9800',
      description: 'Farm lands & agricultural plots'
    }
  ];

  // Get URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const country = urlParams.get('country') || 'India';
    const featured = urlParams.get('featured') || 'true';
    const category = urlParams.get('category') || 'all';

    setFilters({ featured, country });

    // Set active tab based on URL
    const tabIndex = tabs.findIndex(tab => tab.value === category);
    if (tabIndex !== -1) {
      setActiveTab(tabIndex);
    }
  }, [location.search]);

  // Fetch properties count
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await getFeaturedPropertiesCount();
        if (response.success) {
          setCounts(response.data);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchCounts();
  }, []);

  // Fetch properties based on active tab
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const currentTab = tabs[activeTab];
        let response;

        if (currentTab.value === 'all') {
          response = await getFeaturedProperties({ ...filters, limit: 50 });
        } else {
          response = await getFeaturedPropertiesByCategory(currentTab.value, { ...filters, limit: 50 });
        }

        if (Array.isArray(response)) {
          setProperties(response);
          if (response.length === 0) {
            setToastMessage(`No featured ${currentTab.value === 'all' ? '' : currentTab.value} properties found.`);
            setToastSeverity('info');
            setToastOpen(true);
          }
        } else {
          setProperties([]);
          setToastMessage('Failed to load properties. Please try again.');
          setToastSeverity('error');
          setToastOpen(true);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]);
        setToastMessage('Failed to load properties. Please try again.');
        setToastSeverity('error');
        setToastOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [activeTab, filters]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const currentTab = tabs[newValue];

    // Update URL without page reload
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('category', currentTab.value);
    navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  const renderPropertyGrid = () => {
    if (loading) {
      return (
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {[...Array(6)].map((_, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={4}
              key={index}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Card
                className="featured-loading-skeleton"
                sx={{
                  height: 480,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Skeleton
                  variant="rectangular"
                  height={300}
                  width="100%"
                />
                <CardContent sx={{ flex: 1 }}>
                  <Skeleton variant="text" height={30} sx={{ mb: 1 }} />
                  <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" height={20} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Skeleton variant="rectangular" width={60} height={20} />
                    <Skeleton variant="rectangular" width={60} height={20} />
                    <Skeleton variant="rectangular" width={60} height={20} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Skeleton variant="rectangular" width="48%" height={40} />
                    <Skeleton variant="rectangular" width="48%" height={40} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (properties.length === 0) {
      return (
        <Box sx={{ py: 4, width: '100%' }}>
          <NoPropertiesAnimation type={tabs[activeTab].value === 'all' ? 'featured' : `featured ${tabs[activeTab].value}`} />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                backgroundColor: '#d8b035',
                '&:hover': { backgroundColor: '#c5a02f' }
              }}
            >
              Browse All Properties
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Fade in={!loading} timeout={600}>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          {properties.map((property, index) => (
            <Grid
              item
              xs={12}
              sm={viewMode === 'list' ? 12 : 6}
              md={viewMode === 'list' ? 12 : 4}
              lg={viewMode === 'list' ? 12 : 4}
              key={property._id || property.id || index}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <EnhancedPropertyCard
                  property={property}
                  viewMode={viewMode}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Fade>
    );
  };

  const currentTab = tabs[activeTab];

  return (
    <div className="featured-properties-page">
      {/* Page Header */}
      <Pageheader
        headertitle="Featured Properties"
        bgImage={bgImage}
      // subTitle="Explore our carefully curated collection of premium properties across residential, commercial, and agricultural categories"
      />

      {/* Property Filter */}
      {/* <PropertyFilter
        filters={filters}
        onSearch={handleFilterChange}
        disabledFields={['featured']}
        enableNavigation={false}
      /> */}

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Category Tabs */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            mb: 2,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%'
          }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                maxWidth: '100%',
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important'
                },
                '& .MuiTab-root': {
                  minHeight: { xs: 70, md: 80 },
                  minWidth: { xs: 90, md: 120 },
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  fontWeight: 'bold',
                  flexShrink: 0,
                  '&.Mui-selected': {
                    color: currentTab.color
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: currentTab.color,
                  height: 3
                },
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  icon={tab.icon}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', md: '1rem' } }}>
                        {tab.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: { xs: 'none', sm: 'block' } }}>
                        {tab.description}
                      </Typography>
                    </Box>
                  }
                  iconPosition="top"
                />
              ))}
            </Tabs>

            {/* View Mode Toggle */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              justifyContent: { xs: 'center', md: 'flex-end' },
              width: { xs: '100%', md: 'auto' },
              mt: { xs: 1, md: 0 }
            }}>
              <Button
                variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('grid')}
                startIcon={<GridViewIcon />}
                size="small"
                sx={{
                  flex: { xs: 1, md: 'none' },
                  minWidth: { xs: 'auto', md: '100px' },
                  backgroundColor: viewMode === 'grid' ? '#d8b035' : 'transparent',
                  borderColor: '#d8b035',
                  color: viewMode === 'grid' ? 'white' : '#d8b035',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  padding: { xs: '6px 12px', md: '8px 16px' },
                  '&:hover': {
                    backgroundColor: viewMode === 'grid' ? '#c5a02f' : 'rgba(216, 176, 53, 0.1)',
                    borderColor: '#d8b035'
                  }
                }}
              >
                Grid
              </Button>
              <Button
                variant={viewMode === 'list' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('list')}
                startIcon={<ListViewIcon />}
                size="small"
                sx={{
                  flex: { xs: 1, md: 'none' },
                  minWidth: { xs: 'auto', md: '100px' },
                  backgroundColor: viewMode === 'list' ? '#d8b035' : 'transparent',
                  borderColor: '#d8b035',
                  color: viewMode === 'list' ? 'white' : '#d8b035',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  padding: { xs: '6px 12px', md: '8px 16px' },
                  '&:hover': {
                    backgroundColor: viewMode === 'list' ? '#c5a02f' : 'rgba(216, 176, 53, 0.1)',
                    borderColor: '#d8b035'
                  }
                }}
              >
                List
              </Button>
            </Box>
          </Box>

          {/* Current Tab Info */}
          <Alert
            severity="info"
            sx={{
              backgroundColor: `${currentTab.color}15`,
              color: currentTab.color,
              border: `1px solid ${currentTab.color}30`,
              '& .MuiAlert-icon': {
                color: currentTab.color
              }
            }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              <strong>{currentTab.label}:</strong> {currentTab.description}
              {!loading && ` • ${properties.length} properties found`}
              {counts.total > 0 && ` • Total featured: ${counts.total} (${counts.residential} residential, ${counts.commercial} commercial, ${counts.agricultural} agricultural)`}
            </Typography>
          </Alert>
        </Box>

        {/* Properties Grid */}
        <Box className="featured-properties-container">
          {renderPropertyGrid()}
        </Box>

        {/* Load More Button */}
        {!loading && properties.length > 0 && properties.length >= 20 && (
          <Box className="featured-load-more">
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#d8b035',
                color: '#d8b035',
                fontSize: { xs: '0.875rem', md: '1rem' },
                padding: { xs: '8px 16px', md: '12px 24px' },
                '&:hover': {
                  borderColor: '#c5a02f',
                  backgroundColor: '#d8b035',
                  color: 'white'
                }
              }}
            >
              Load More Properties
            </Button>
          </Box>
        )}
      </Container>

      {/* Toast Notifications */}
      <Toast
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </div>
  );
};

export default FeaturedPropertiesPage;